import { CommonModule } from '@angular/common';
import { Component, inject, OnInit, signal } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { FormsModule } from '@angular/forms';
import { debounceTime, Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { ChartModule } from 'primeng/chart';
import { SelectModule } from 'primeng/select';
import { SkeletonModule } from 'primeng/skeleton';
import { LayoutService } from '@/layout/service/layout.service';
import { ContentHeaderComponent } from '@/shared/components/content-header/content-header.component';
import { CStatsWidget } from './components/c-statswidget/c-statswidget';
import { CAiSummaryComponent } from './components/c-ai-summary/c-ai-summary.component';
import { AI_FEATURE_ENABLED } from '@/shared/utilities/feature-flags';
import { CardComponent } from '@/shared/ui-components/card/card.component';
import { DoughnutChartComponent } from '@/shared/ui-components/charts/doughnut-chart/doughnut-chart.component';
import { TagComponent } from '@/shared/ui-components/tag/tag.component';
import { ProgressBarComponent } from '@/shared/ui-components/progress-bar/progress-bar.component';
import { StepperComponent } from '@/shared/ui-components/stepper/stepper.component';
import { Step } from '@/shared/models/stepper.model';
import { DividerComponent } from "@/shared/ui-components/divider/divider.component";
import { ButtonComponent } from '@/shared/ui-components/button/button.component';
import { TagSeverity } from '@/shared/enums/generic.enum';
import { DealsApiService } from '@/pages/deals/data-access/deals-api.service';
import { ClientDeal } from '@/pages/deals/data-access/clientdeals.model';
import { assetClasses, getSubAssetClassLabelKey, productTypes } from '@/pages/deals/data-access/deals.option';
import { CurrencyDisplayComponent } from '@/shared/ui-components/currency-display/currency-display.component';
import type { AssetClass } from '@/pages/deals/data-access/deals.enum';
import { ClientAssetClassChartDto, FeaturedDealCard } from './data-access/client-dashboard.model';
import { ExpressInterestDialogComponent } from '@/shared/ui-components/express-interest-dialog/express-interest-dialog.component';
import { NotInterestedDialogComponent } from '@/shared/ui-components/not-interested-dialog/not-interested-dialog.component';
import { HelpersService } from '@/shared/services/helpers.service';
import { ClientDealStatusV2 } from '@/shared/enums/generic.model';
import { PaginatedResponse } from '@/shared/models/paginated-response.model';
import { PaginatedGridComponent } from '@/shared/ui-components/paginated-grid/paginated-grid.component';
import { DrawerComponent } from '@/shared/ui-components/drawer/drawer.component';
import { DrawerService } from '@/shared/ui-components/drawer/drawer.service';
import { FileUploaderComponent } from '@/shared/ui-components/file-uploader/file-uploader.component';
import { DocPreviewDialogComponent } from '@/shared/ui-components/doc-preview-dialog/doc-preview-dialog.component';
import { canClientDeleteKycDocument, ClientKycRequirement, ClientKycRequirementFile, ClientKycSession, ClientKycType, isClientKycRequirementMandatory, sessionClientKycProfileId } from '@/pages/deals/data-access/clientdeals.model';
import { ConfirmationDialogComponent } from '@/shared/components/confirmation-dialog/confirmation-dialog.component';
import { ClientKycStatus } from '@/shared/enums/generic.enum';
import { AsyncState } from '@/shared/models/async-state.model';
import { ClientDashboardService } from './data-access/client-dashboard-api.service';
import { UserProfileService } from '@/pages/user-profile/user-profile.service';

@Component({
    selector: 'app-client-dashboard',
    standalone: true,
    imports: [
        CommonModule,
        RouterLink,
        TranslateModule,
        AngularSvgIconModule,
        FormsModule,
        ChartModule,
        SelectModule,
        SkeletonModule,
        ContentHeaderComponent,
        CStatsWidget,
        CAiSummaryComponent,
        CardComponent,
        DoughnutChartComponent,
        TagComponent,
        ProgressBarComponent,
        StepperComponent,
        DividerComponent,
        ButtonComponent,
        CurrencyDisplayComponent,
        ExpressInterestDialogComponent,
        NotInterestedDialogComponent,
        PaginatedGridComponent,
        DrawerComponent,
        FileUploaderComponent,
        DocPreviewDialogComponent,
        ConfirmationDialogComponent,
    ],
    templateUrl: './client-dashboard.component.html',
    styles: ``
})
export class CDashboardComponent implements OnInit {
    protected readonly aiFeatureEnabled = AI_FEATURE_ENABLED;
    protected readonly isClientKycRequirementMandatory = isClientKycRequirementMandatory;
    lineData: any;
    barData: any;
    pieData: any;
    polarData: any;
    radarData: any;
    lineOptions: any;
    barOptions: any;
    pieOptions: any;
    polarOptions: any;
    radarOptions: any;
    doughnutChartData: any;
    // Request Deal Dialog
    public showRequestDealDialog: boolean = false;

    public readonly assetClassChartState = signal<AsyncState<ClientAssetClassChartDto>>({
        data: null,
        loading: false,
        error: null,
    });

    private static readonly featuredDealsPageSizeDefault = 4;

    readonly featuredDealsData = signal<PaginatedResponse<FeaturedDealCard> | null>(null);
    readonly featuredDealsLoading = signal<boolean>(false);
    readonly showExpressInterestDialog = signal<boolean>(false);
    readonly selectedDealForExpressInterest = signal<FeaturedDealCard | null>(null);
    readonly showWithdrawInterestDialog = signal<boolean>(false);
    readonly selectedDealForWithdrawInterest = signal<FeaturedDealCard | null>(null);

    private readonly configUpdateSub: Subscription;

    private readonly _layoutService = inject(LayoutService);
    private readonly _dealsApi = inject(DealsApiService);
    private readonly _translate = inject(TranslateService);
    private readonly _helpers = inject(HelpersService);
    private readonly _drawerService = inject(DrawerService);
    private readonly _dealsApiService = inject(DealsApiService);
    private readonly _clientDashboardApi = inject(ClientDashboardService);
    private readonly _userProfileService = inject(UserProfileService);
    private readonly _router = inject(Router);

    // --- Request KYC drawer (dashboard) ---
    private readonly clientDashboardKycRequestDrawerId = 'clientDashboardKycRequestDrawer';
    readonly selectedDealForKyc = signal<FeaturedDealCard | null>(null);
    readonly kycStep = signal<number>(0);
    readonly selectedKycType = signal<ClientKycType>(0);
    readonly kycSession = signal<ClientKycSession | null>(null);
    readonly kycSessionLoading = signal<boolean>(false);
    readonly kycSubmitting = signal<boolean>(false);
    readonly kycUploadLoadingByReq = signal<Record<number, boolean>>({});
    readonly kycPreviewVisible = signal<boolean>(false);
    readonly kycFileDeleteTarget = signal<{ requirementId: number; fileId: number } | null>(null);
    readonly kycFileDeleteLoading = signal(false);
    kycPreviewUrl: string | null = null;
    kycPreviewFileName: string | null = null;

    readonly ClientDealStatusV2 = ClientDealStatusV2

    public readonly kycSteps = [
        { label: 'KYC Type' },
        { label: 'Upload Documents' },
    ] as Step[];

    public readonly kycTypeOptions: { label: string; value: ClientKycType }[] = [
        { label: 'Individual', value: 0 },
        { label: 'Corporate', value: 1 },
    ];

    constructor() {
        this.configUpdateSub = this._layoutService.configUpdate$.pipe(debounceTime(25)).subscribe(() => {
            this.initCharts();
        });
    }

    public ngOnInit() {
        this._userProfileService.getUserProfile();
        this.loadAssetClassChart();
        this.initCharts();
        this.loadFeaturedDeals(1, CDashboardComponent.featuredDealsPageSizeDefault);
    }

    public get dashboardHeaderTitle(): string {
        const profile = this._userProfileService.userProfileData();
        const baseTitle = this._translate.instant('CLIENTS.DASHBOARD.TITLE') as string;
        const fullName = `${profile?.firstName ?? ''} ${profile?.lastName ?? ''}`.trim() || profile?.name || '';
        const roleLabel = profile?.roleLabel ? (this._translate.instant(profile.roleLabel) as string) : '';
        return [baseTitle, fullName, roleLabel].filter(Boolean).join(' - ');
    }

    private loadAssetClassChart(): void {
        this.assetClassChartState.update((s) => ({ ...s, loading: true, error: null }));
        this._clientDashboardApi
            .getMyInvestmentsByAssetClassChart()
            .pipe(finalize(() => this.assetClassChartState.update((s) => ({ ...s, loading: false }))))
            .subscribe({
                next: (data) => {
                    this.assetClassChartState.set({ data, loading: false, error: null });
                    this.buildAssetClassDoughnutChart(data);
                },
                error: (err: unknown) => {
                    this.assetClassChartState.set({ data: null, loading: false, error: err });
                    this.doughnutChartData = { labels: [], datasets: [{ data: [], backgroundColor: [], hoverBackgroundColor: [] }] };
                },
            });
    }

    public featuredDealsTotal(): number {
        return this.featuredDealsData()?.total ?? 0;
    }

    public featuredDealsItems(): FeaturedDealCard[] {
        return this.featuredDealsData()?.items ?? [];
    }

    public featuredDealsFirst(): number {
        const d = this.featuredDealsData();
        const page = d?.page ?? 1;
        const pageSize = d?.pageSize ?? CDashboardComponent.featuredDealsPageSizeDefault;
        return (page - 1) * pageSize;
    }

    public featuredDealsPageSize(): number {
        return this.featuredDealsData()?.pageSize ?? CDashboardComponent.featuredDealsPageSizeDefault;
    }

    public getFeaturedDealsEmptyMessage(): string {
        return this._translate.instant('CLIENTS.DASHBOARD.NO_FEATURED_DEALS');
    }

    public onFeaturedDealsPageChange(event: { first: number; rows: number }): void {
        const page = Math.floor(event.first / event.rows) + 1;
        this.loadFeaturedDeals(page, event.rows);
    }

    private loadFeaturedDeals(page = 1, pageSize = CDashboardComponent.featuredDealsPageSizeDefault): void {
        this.featuredDealsLoading.set(true);
        this._dealsApi
            .getClientDeals(page, pageSize, ClientDealStatusV2.LIVE)
            .pipe(finalize(() => this.featuredDealsLoading.set(false)))
            .subscribe({
                next: (res) => {
                    this.featuredDealsData.set({
                        items: [...res.items]
                            .sort((a, b) => this.compareDealsByCloseDateAndCreatedAt(a, b))
                            .map((d) => this.mapClientDealToFeaturedCard(d)),
                        total: res.total,
                        page: res.page,
                        pageSize: res.pageSize,
                    });
                },
                error: () => {
                    this.featuredDealsData.set({
                        items: [],
                        total: 0,
                        page: 1,
                        pageSize,
                    });
                },
            });
    }

    private mapClientDealToFeaturedCard(d: ClientDeal): FeaturedDealCard {
        const categories = this.getCategoriesFromDeal(d);
        const progress = d.clientDealPerformance?.progressPercent ?? 0;
        const remainingAllocation = Math.max(0, 100 - progress);
        const tag = this.getDealTag(d);
        const tagSeverity = this.getDealTagSeverity(d);
        const duration = d.dealTermMonths ? `${d.dealTermMonths} months` : '—';
        const sections = (d.sections ?? []) as { title?: string; content?: string }[];
        const highlights = sections
            .filter((s) => s && (s.title || s.content))
            .map((s) => s.title || String(s.content ?? '').slice(0, 80) || '—');
        const stepperCurrentStep = this.computeInvestmentJourneyStep(d);
        const stepperErrorStepIndex = this.computeInvestmentJourneyErrorStepIndex(d);

        // By default, infer all indices < current step as completed,
        // then "de-infer" the earlier steps that are NOT actually completed.
        const completedStepIndices: number[] = Array.from({ length: stepperCurrentStep }, (_, i) => i);

        // Step 0 completion: "Express Interest" should be considered completed only when clientDealStatus matches.
        if (Number(d.clientDealStatus) !== ClientDealStatusV2.EXPRESSED_INTEREST) {
            const idx = completedStepIndices.indexOf(0);
            if (idx !== -1) completedStepIndices.splice(idx, 1);
        }

        // Step 1 completion: minThresholdMet drives the "Min Threshold" progress.
        if (d.minThresholdMet !== true) {
            const idx = completedStepIndices.indexOf(1);
            if (idx !== -1) completedStepIndices.splice(idx, 1);
        }

        // Step 2 completion: KYC "tick" only for Submitted/Under review/Approved.
        const kycStatus = d.clientKyc != null ? Number(d.clientKyc.status) : null;
        const isKycCompleted =
            kycStatus != null &&
            [ClientKycStatus.SUBMITTED, ClientKycStatus.UNDER_REVIEW, ClientKycStatus.APPROVED].includes(kycStatus);
        if (!isKycCompleted) {
            const idx = completedStepIndices.indexOf(2);
            if (idx !== -1) completedStepIndices.splice(idx, 1);
        }

        return {
            id: d.id ?? d.clientDealId ?? 0,
            clientDealId: d.clientDealId,
            clientDealStatus: (d.clientDealStatus ?? ClientDealStatusV2.LIVE) as ClientDealStatusV2,
            minThresholdMet: d.minThresholdMet === true,
            clientKycStatus: d.clientKyc != null ? Number(d.clientKyc.status) : null,
            assetClass: d.assetClass ?? 0,
            title: d.name ?? '—',
            createdAt: d.createdAt ?? null,
            closeDate: d.closeDate ?? null,
            categories,
            tag,
            tagSeverity,
            remainingAllocation: Math.round(remainingAllocation * 100) / 100,
            progressValue: progress,
            minimumAmountUsd:
                d.minimumAmountUsd ??
                (d as unknown as { MinimumAmountUsd?: number }).MinimumAmountUsd ??
                0,
            currency: d.currency,
            targetReturn: d.targetReturn != null ? `${d.targetReturn}%` : '—',
            duration,
            // riskLevel: '—',
            description: d.description ?? '—',
            highlights,
            managerName: d.relationshipManagerName ?? 'Your RM',
            managerMessage: '',
            stepperCurrentStep,
            stepperErrorStepIndex,
            stepperCompletedStepIndices: completedStepIndices,
            sections: sections ?? [],
            rmNote: d.rmNote ?? null,
            kycType: d.kycType ?? null,
            clientInterest: d.clientInterest ?? null,
        };
    }

    private compareDealsByCloseDateAndCreatedAt(a: ClientDeal, b: ClientDeal): number {
        const aCloseDate = this.toValidDate(a.closeDate);
        const bCloseDate = this.toValidDate(b.closeDate);

        if (aCloseDate && bCloseDate) {
            const closeDateDiff = aCloseDate.getTime() - bCloseDate.getTime();
            if (closeDateDiff !== 0) return closeDateDiff;
        } else if (aCloseDate) {
            return -1;
        } else if (bCloseDate) {
            return 1;
        }

        const aCreatedAt = this.toValidDate(a.createdAt)?.getTime() ?? 0;
        const bCreatedAt = this.toValidDate(b.createdAt)?.getTime() ?? 0;
        return bCreatedAt - aCreatedAt;
    }

    public kycActionLabel(deal: FeaturedDealCard): string {
        return Number(deal?.clientKycStatus) === ClientKycStatus.RESUBMISSION_REQUIRED ? 'Resubmit KYC' : 'Submit KYC';
    }

    public canShowRequestKycButton(deal: FeaturedDealCard): boolean {
        const clientDealStatus = Number(deal?.clientDealStatus);
        const kycStatus = Number(deal?.clientKycStatus ?? 0);
        const isEligibleStage =
            clientDealStatus === ClientDealStatusV2.EXPRESSED_INTEREST ||
            clientDealStatus === ClientDealStatusV2.CLOSING;

        return (
            isEligibleStage &&
            deal?.minThresholdMet === true &&
            (kycStatus === ClientKycStatus.PENDING ||
                kycStatus === ClientKycStatus.RESUBMISSION_REQUIRED)
        );
    }

    /** Stepper bounce when Express Interest or Submit KYC is shown for this deal card. */
    public shouldEnableInvestmentJourneyStepperBounce(deal: FeaturedDealCard): boolean {
        return (
            this.shouldShowExpressInterestButton(deal.clientDealStatus) ||
            this.canShowRequestKycButton(deal)
        );
    }

    public openRequestKycDrawer(deal: FeaturedDealCard): void {
        if (!deal?.clientDealId) return;
        if (Number(deal.clientKycStatus) === ClientKycStatus.RESUBMISSION_REQUIRED) {
            void this._router.navigate(['/client', 'deal-details', deal.clientDealId], {
                queryParams: {
                    dealId: deal.id,
                    tab: 'client-kyc',
                    openKycResubmit: '1',
                },
            });
            return;
        }
        this.selectedDealForKyc.set(deal);
        this.kycStep.set(0);
        this.selectedKycType.set(0);
        this.kycSession.set(null);
        this._drawerService.open(this.clientDashboardKycRequestDrawerId);
        // Only call the session API if the parent DTO includes a kycType.
        const clientDealId = Number(deal?.clientDealId ?? 0);
        if (!clientDealId) return;

        const parentKycType = (deal as any)?.kycType;
        if (parentKycType == null) {
            // No kycType present on the parent — keep the user on the selection step.
            return;
        }

        // Preselect the kyc type from the parent and jump to step 2, then load an existing session.
        this.selectedKycType.set(parentKycType);
        this.kycStep.set(1);
        this.kycSessionLoading.set(true);
        this._dealsApiService
            .getClientKycSession(clientDealId)
            .pipe(finalize(() => this.kycSessionLoading.set(false)))
            .subscribe({
                next: (res) => {
                    const session = ((res as any)?.data ?? res ?? null) as ClientKycSession | null;
                    if (session) {
                        this.kycSession.set(session);
                    } else {
                        // No session found; fall back to selection step.
                        this.kycStep.set(0);
                    }
                },
                error: () => {
                    // If session load fails, fall back to selection step.
                    this.kycStep.set(0);
                },
            });
    }

    public closeRequestKycDrawer(): void {
        this._drawerService.close(this.clientDashboardKycRequestDrawerId);
        this.selectedDealForKyc.set(null);
        this.kycSession.set(null);
        this.kycStep.set(0);
        this.kycUploadLoadingByReq.set({});
    }

    public createKycSession(): void {
        const deal = this.selectedDealForKyc();
        const clientDealId = Number(deal?.clientDealId ?? 0);
        if (!clientDealId || this.kycSessionLoading()) return;
        this.kycSessionLoading.set(true);
        this._dealsApiService
            .createClientKycSession(clientDealId, this.selectedKycType())
            .pipe(finalize(() => this.kycSessionLoading.set(false)))
            .subscribe({
                next: (res) => {
                    // Backend/interceptors may return either { data: session } or session directly.
                    const session = ((res as any)?.data ?? res ?? null) as ClientKycSession | null;
                    this.kycSession.set(session);
                    this.kycStep.set(1);
                    // Fetch previewUrl for any pre-existing uploaded files.
                    this.refreshKycSession();
                },
                error: () => this._helpers.showErrorMessage('Failed to create KYC session'),
            });
    }

    public refreshKycSession(): void {
        const deal = this.selectedDealForKyc();
        const clientDealId = Number(deal?.clientDealId ?? 0);
        if (!clientDealId) return;
        this.kycSessionLoading.set(true);
        this._dealsApiService
            .getClientKycSession(clientDealId)
            .pipe(finalize(() => this.kycSessionLoading.set(false)))
            .subscribe({
                next: (res) => {
                    // Backend/interceptors may return either { data: session } or session directly.
                    const session = ((res as any)?.data ?? res ?? null) as ClientKycSession | null;
                    if (session) this.kycSession.set(session);
                },
                error: () => {
                    // Keep the last known session so the UI doesn't fall back to "Unable to load session".
                    this._helpers.showErrorMessage('Failed to refresh session');
                },
            });
    }

    public requirementTitle(req: ClientKycRequirement): string {
        const map: Record<number, string> = {
            0: 'Passport',
            1: 'Proof of Address',
            2: 'Proof of Funds',
            3: 'Articles of Association',
            4: 'Company Register Extract',
            5: 'Ownership Structure Chart',
            6: 'Share Register',
            7: 'Authorised Signatories POA',
            8: 'Financial Statement',
            9: 'Bank Statement',
            10: 'UBO Passport',
            11: 'UBO Proof of Address',
            12: 'Other',
        };
        return map[Number(req?.requirementType ?? -1)] ?? `Requirement ${req?.requirementType ?? ''}`;
    }

    public uploadRequirementFiles(req: ClientKycRequirement, files: File[], uploader?: FileUploaderComponent): void {
        const deal = this.selectedDealForKyc();
        const clientDealId = Number(deal?.clientDealId ?? 0);
        const requirementId = Number(req?.id ?? 0);
        if (!clientDealId || !requirementId || !files?.length) return;
        if (this.kycUploadLoadingByReq()[requirementId]) return;
        const filesToUpload = [...files];
        uploader?.clear();
        this.kycUploadLoadingByReq.update((m) => ({ ...m, [requirementId]: true }));
        const uploadOneByOne = (fs: File[], idx: number): void => {
            if (idx >= fs.length) {
                this.kycUploadLoadingByReq.update((m) => ({ ...m, [requirementId]: false }));
                this.refreshKycSession();
                return;
            }
            this._dealsApiService.uploadClientKycRequirementFile(clientDealId, requirementId, fs[idx]).subscribe({
                next: () => uploadOneByOne(fs, idx + 1),
                error: () => {
                    this.kycUploadLoadingByReq.update((m) => ({ ...m, [requirementId]: false }));
                    this._helpers.showErrorMessage('File upload failed');
                },
            });
        };
        uploadOneByOne(filesToUpload, 0);
    }

    public canDeleteKycFile(): boolean {
        return canClientDeleteKycDocument(this.kycSession()?.status);
    }

    public requestDeleteKycFile(req: ClientKycRequirement, file: ClientKycRequirementFile, event: Event): void {
        event.stopPropagation();
        if (!this.canDeleteKycFile() || this.kycFileDeleteLoading()) return;
        this.kycFileDeleteTarget.set({ requirementId: Number(req.id), fileId: Number(file.id) });
    }

    public cancelDeleteKycFile(): void {
        if (this.kycFileDeleteLoading()) return;
        this.kycFileDeleteTarget.set(null);
    }

    public confirmDeleteKycFile(): void {
        const target = this.kycFileDeleteTarget();
        const clientDealId = Number(this.selectedDealForKyc()?.clientDealId ?? 0);
        const profileId = sessionClientKycProfileId(this.kycSession());
        if (!target || this.kycFileDeleteLoading()) return;
        if (!profileId && !clientDealId) return;
        this.kycFileDeleteLoading.set(true);
        const dropFile = (): void => {
            this.kycSession.update((current) => {
                if (!current) return current;
                return {
                    ...current,
                    requirements: (current.requirements ?? []).map((req) =>
                        Number(req.id) === target.requirementId
                            ? { ...req, files: (req.files ?? []).filter((file) => Number(file.id) !== target.fileId) }
                            : req,
                    ),
                };
            });
        };
        const done = (): void => {
            this.kycFileDeleteLoading.set(false);
            this.kycFileDeleteTarget.set(null);
            this._helpers.showSuccessMessage('Document deleted');
        };
        const failed = (): void => this.kycFileDeleteLoading.set(false);
        if (profileId) {
            this._dealsApiService.deleteClientKycProfileFile(profileId, target.requirementId, target.fileId).subscribe({
                next: () => {
                    dropFile();
                    done();
                },
                error: failed,
            });
            return;
        }
        this._dealsApiService.deleteClientKycDealFile(clientDealId, target.requirementId, target.fileId).subscribe({
            next: (session) => {
                if (session?.requirements) this.kycSession.set(session);
                else dropFile();
                done();
            },
            error: failed,
        });
    }

    public openKycFilePreview(url: string | null | undefined, fileName: string): void {
        const u = String(url ?? '').trim();
        if (!u) return;
        this.kycPreviewUrl = u;
        this.kycPreviewFileName = fileName;
        this.kycPreviewVisible.set(true);
    }

    public kycFileTypeIcon(fileName: string | null | undefined): string {
        const n = String(fileName ?? '').toLowerCase();
        if (n.endsWith('.pdf')) return 'pi pi-file-pdf';
        if (n.endsWith('.doc') || n.endsWith('.docx')) return 'pi pi-file-word';
        return 'pi pi-file';
    }

    public canSubmitKyc(): boolean {
        const session = this.kycSession();
        const reqs = session?.requirements ?? [];
        if (!reqs.length) return false;
        return reqs
            .filter((r) => isClientKycRequirementMandatory(r))
            .every((r) => Array.isArray(r.files) && r.files.length > 0);
    }

    public submitKyc(): void {
        const deal = this.selectedDealForKyc();
        const clientDealId = Number(deal?.clientDealId ?? 0);
        if (!clientDealId || this.kycSubmitting() || !this.canSubmitKyc()) return;
        this.kycSubmitting.set(true);
        this._dealsApiService
            .submitClientKyc(clientDealId)
            .pipe(finalize(() => this.kycSubmitting.set(false)))
            .subscribe({
                next: () => {
                    this._helpers.showSuccessMessage('KYC submitted successfully');
                    this.closeRequestKycDrawer();
                    const d = this.featuredDealsData();
                    this.loadFeaturedDeals(d?.page ?? 1, d?.pageSize ?? CDashboardComponent.featuredDealsPageSizeDefault);
                },
                error: () => this._helpers.showErrorMessage('Failed to submit KYC'),
            });
    }

    /**
     * Active step index (0–4). Earlier steps show completed when their index is below currentStep.
     * 0 Express Interest — default (not yet expressed)
     * 1 Min Threshold — clientDealStatus === 1 (ExpressedInterest): step 0 completed, working on threshold
     * 2 KYC — minThresholdMet: steps 0–1 completed, KYC active
     * 3 Subscription — clientKyc.status === 3
     * 4 Capital — dealSubscription.status === 3, then capitalStatus.status === 3
     * Furthest milestone wins.
     */
    private computeInvestmentJourneyStep(d: ClientDeal): number {
        let step = 0;
        if (Number(d.clientDealStatus) === 1) {
            step = Math.max(step, 1);
        }
        if (d.minThresholdMet === true) {
            step = Math.max(step, 2);
        }
        // KYC shows completed (tick) for Submitted / Under review / Approved.
        if (
            d.clientKyc != null &&
            [
                ClientKycStatus.SUBMITTED,
                ClientKycStatus.UNDER_REVIEW,
                ClientKycStatus.APPROVED,
            ].includes(Number(d.clientKyc.status))
        ) {
            step = Math.max(step, 3);
        }
        if (d.dealSubscription != null && Number(d.dealSubscription.status) === 3) {
            step = Math.max(step, 4);
        }
        if (d.capitalStatus != null && Number(d.capitalStatus.status) === 3) {
            step = Math.max(step, 4);
        }
        return step;
    }

    /** Mark the KYC step (index 2) as error when KYC is rejected. */
    private computeInvestmentJourneyErrorStepIndex(d: ClientDeal): number | undefined {
        if (d?.clientKyc != null && Number(d.clientKyc.status) === 3) {
            return 2; // KYC Check step index
        }
        return undefined;
    }

    private getCategoriesFromDeal(d: ClientDeal): string[] {
        const parts: string[] = [];
        if (d.assetClass != null) {
            const ac = assetClasses.find((o) => o.value === d.assetClass);
            parts.push(ac ? this._translate.instant(ac.labelKey) : '—');
        }
        if (d.assetSubClass != null) {
            const subLabelKey = getSubAssetClassLabelKey(d.assetSubClass);
            parts.push(subLabelKey ? this._translate.instant(subLabelKey) : '—');
        }
        if (d.productType != null) {
            const pt = productTypes.find((o) => o.value === d.productType);
            parts.push(pt ? this._translate.instant(pt.labelKey) : '—');
        }
        return parts.length ? parts : ['—'];
    }

    private getDealTag(d: ClientDeal): string {
        if(d.clientDealStatus === ClientDealStatusV2.EXPRESSED_INTEREST){
            return 'Interested';
        }
        const closeDate = d.closeDate;
        if (closeDate) {
            const d2 = typeof closeDate === 'string' ? new Date(closeDate) : closeDate;
            if (!isNaN(d2.getTime())) {
                const day = String(d2.getDate()).padStart(2, '0');
                const month = String(d2.getMonth() + 1).padStart(2, '0');
                const year = d2.getFullYear();
                return `Closing on ${day}/${month}/${year}`;
            }
        }
        return 'New';
    }

    private getDealTagSeverity(d: ClientDeal): TagSeverity {
        if(d.clientDealStatus === ClientDealStatusV2.EXPRESSED_INTEREST){
            return TagSeverity.SECONDARY;
        }
        const closeDate = d.closeDate;
        if (closeDate) {
            const d2 = typeof closeDate === 'string' ? new Date(closeDate) : closeDate;
            if (!isNaN(d2.getTime())) {
                const daysUntilClose = Math.ceil((d2.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
                if (daysUntilClose <= 14) return TagSeverity.HELP;
            }
        }
        return TagSeverity.PRIMARY;
    }

    onExpressInterest(deal: FeaturedDealCard): void {        this.selectedDealForExpressInterest.set(deal);
        this.showExpressInterestDialog.set(true);
    }

    onExpressInterestDialogVisibleChange(visible: boolean): void {
        this.showExpressInterestDialog.set(visible);
        if (!visible) this.selectedDealForExpressInterest.set(null);
    }

    onExpressInterestSuccess(): void {
        this._helpers.showSuccessMessage('Interest expressed successfully.');
        this.selectedDealForExpressInterest.set(null);
        this.showExpressInterestDialog.set(false);
        const d = this.featuredDealsData();
        this.loadFeaturedDeals(d?.page ?? 1, d?.pageSize ?? CDashboardComponent.featuredDealsPageSizeDefault);
    }

    shouldShowExpressInterestButton(status: ClientDealStatusV2): boolean {
        return status !== ClientDealStatusV2.EXPRESSED_INTEREST;
    }

    private toValidDate(value: string | Date | null | undefined): Date | null {
        if (!value) return null;
        const date = value instanceof Date ? value : new Date(value);
        return isNaN(date.getTime()) ? null : date;
    }

    onClientWithdrawInterest(deal: FeaturedDealCard): void {        this.selectedDealForWithdrawInterest.set(deal);
        this.showWithdrawInterestDialog.set(true);
    }

    onWithdrawInterestDialogVisibleChange(visible: boolean): void {
        this.showWithdrawInterestDialog.set(visible);
        if (!visible) this.selectedDealForWithdrawInterest.set(null);
    }

    onClientWithdrawInterestSuccess(): void {
        this._helpers.showSuccessMessage('Interest withdrawn successfully.');
        this.selectedDealForWithdrawInterest.set(null);
        this.showWithdrawInterestDialog.set(false);
        const d = this.featuredDealsData();
        this.loadFeaturedDeals(d?.page ?? 1, d?.pageSize ?? CDashboardComponent.featuredDealsPageSizeDefault);
    }

    getAssetClassIcon(assetClass: number | undefined): string {
        const icons: Record<number, string> = {
            0: '/icons/private_equity.svg',
            1: '/icons/private_credit.svg',
            2: '/icons/digital_assets.svg',
            3: '/icons/real_estate.svg',
            4: '/icons/commodities.svg',
            5: '/icons/collectibles.svg',
        };
        return icons[assetClass ?? 0] ?? '/icons/private_credit.svg';
    }

    private initCharts(): void {
        const documentStyle = getComputedStyle(document.documentElement);
        const textColor = documentStyle.getPropertyValue('--text-color');
        const textColorSecondary = documentStyle.getPropertyValue('--text-color-secondary');
        const surfaceBorder = documentStyle.getPropertyValue('--surface-border');

        this.barData = {
            labels: ['January', 'February', 'March', 'April', 'May', 'June', 'July'],
            datasets: [
                {
                    label: 'Entity',
                    backgroundColor: documentStyle.getPropertyValue('--p-primary-500'),
                    borderColor: documentStyle.getPropertyValue('--p-primary-500'),
                    data: [65, 59, 80, 81, 56, 55, 40]
                },
                {
                    label: 'Individual',
                    backgroundColor: documentStyle.getPropertyValue('--p-primary-200'),
                    borderColor: documentStyle.getPropertyValue('--p-primary-200'),
                    data: [28, 48, 40, 19, 86, 27, 90]
                }
            ]
        };

        this.barOptions = {
            maintainAspectRatio: false,
            aspectRatio: 0.8,
            plugins: {
                legend: {
                    labels: {
                        color: textColor
                    }
                }
            },
            scales: {
                x: {
                    ticks: {
                        color: textColorSecondary,
                        font: {
                            weight: 500
                        }
                    },
                    grid: {
                        display: false,
                        drawBorder: false
                    }
                },
                y: {
                    ticks: {
                        color: textColorSecondary
                    },
                    grid: {
                        color: surfaceBorder,
                        drawBorder: false
                    }
                }
            }
        };

        this.pieData = {
            labels: ['Draft', 'Submitted', 'Under review', 'Approved', 'Rejected'],
            datasets: [
                {
                    data: [540, 325, 702, 1509, 10],
                    backgroundColor: [
                        documentStyle.getPropertyValue('--p-indigo-500'),
                        documentStyle.getPropertyValue('--p-indigo-700'),
                        documentStyle.getPropertyValue('--p-purple-500'),
                        documentStyle.getPropertyValue('--p-teal-500'),
                        documentStyle.getPropertyValue('--p-teal-700')
                    ],
                    hoverBackgroundColor: [
                        documentStyle.getPropertyValue('--p-indigo-400'),
                        documentStyle.getPropertyValue('--p-indigo-600'),
                        documentStyle.getPropertyValue('--p-purple-400'),
                        documentStyle.getPropertyValue('--p-teal-400'),
                        documentStyle.getPropertyValue('--p-teal-600')
                    ]
                }
            ]
        };

        this.pieOptions = {
            plugins: {
                legend: {
                    labels: {
                        usePointStyle: true,
                        color: textColor
                    }
                }
            }
        };

        this.lineData = {
            labels: ['January', 'February', 'March', 'April', 'May', 'June', 'July'],
            datasets: [
                {
                    label: 'Primary',
                    data: [65, 59, 80, 81, 56, 55, 40],
                    fill: false,
                    backgroundColor: documentStyle.getPropertyValue('--p-primary-500'),
                    borderColor: documentStyle.getPropertyValue('--p-primary-500'),
                    tension: 0.4
                },
                {
                    label: 'Secondary',
                    data: [28, 48, 40, 19, 86, 27, 90],
                    fill: false,
                    backgroundColor: documentStyle.getPropertyValue('--p-primary-200'),
                    borderColor: documentStyle.getPropertyValue('--p-primary-200'),
                    tension: 0.4
                }
            ]
        };

        this.lineOptions = {
            maintainAspectRatio: false,
            aspectRatio: 0.8,
            plugins: {
                legend: {
                    labels: {
                        color: textColor
                    }
                }
            },
            scales: {
                x: {
                    ticks: {
                        color: textColorSecondary
                    },
                    grid: {
                        color: surfaceBorder,
                        drawBorder: false
                    }
                },
                y: {
                    ticks: {
                        color: textColorSecondary
                    },
                    grid: {
                        color: surfaceBorder,
                        drawBorder: false
                    }
                }
            }
        };

        this.polarData = {
            datasets: [
                {
                    data: [11, 16, 7, 3],
                    backgroundColor: [documentStyle.getPropertyValue('--p-indigo-500'), documentStyle.getPropertyValue('--p-purple-500'), documentStyle.getPropertyValue('--p-teal-500'), documentStyle.getPropertyValue('--p-orange-500')],
                    label: 'My dataset'
                }
            ],
            labels: ['Indigo', 'Purple', 'Teal', 'Orange']
        };

        this.polarOptions = {
            plugins: {
                legend: {
                    labels: {
                        color: textColor
                    }
                }
            },
            scales: {
                r: {
                    grid: {
                        color: surfaceBorder
                    },
                    ticks: {
                        display: false,
                        color: textColorSecondary
                    }
                }
            }
        };

        this.radarData = {
            labels: ['Eating', 'Drinking', 'Sleeping', 'Designing', 'Coding', 'Cycling', 'Running'],
            datasets: [
                {
                    label: 'My First dataset',
                    borderColor: documentStyle.getPropertyValue('--p-indigo-400'),
                    pointBackgroundColor: documentStyle.getPropertyValue('--p-indigo-400'),
                    pointBorderColor: documentStyle.getPropertyValue('--p-indigo-400'),
                    pointHoverBackgroundColor: textColor,
                    pointHoverBorderColor: documentStyle.getPropertyValue('--p-indigo-400'),
                    data: [65, 59, 90, 81, 56, 55, 40]
                },
                {
                    label: 'My Second dataset',
                    borderColor: documentStyle.getPropertyValue('--p-purple-400'),
                    pointBackgroundColor: documentStyle.getPropertyValue('--p-purple-400'),
                    pointBorderColor: documentStyle.getPropertyValue('--p-purple-400'),
                    pointHoverBackgroundColor: textColor,
                    pointHoverBorderColor: documentStyle.getPropertyValue('--p-purple-400'),
                    data: [28, 48, 40, 19, 96, 27, 100]
                }
            ]
        };

        this.radarOptions = {
            plugins: {
                legend: {
                    labels: {
                        color: textColor
                    }
                }
            },
            scales: {
                r: {
                    pointLabels: {
                        color: textColor
                    },
                    grid: {
                        color: surfaceBorder
                    }
                }
            }
        };

        const chart = this.assetClassChartState().data;
        if (chart) this.buildAssetClassDoughnutChart(chart, documentStyle);
    }

    private buildAssetClassDoughnutChart(chart: ClientAssetClassChartDto, documentStyle?: CSSStyleDeclaration): void {
        const style = documentStyle ?? getComputedStyle(document.documentElement);
        const slices = chart?.slices ?? [];
        if (!slices.length) {
            this.doughnutChartData = { labels: [], datasets: [{ data: [], backgroundColor: [], hoverBackgroundColor: [] }] };
            return;
        }
        const labels = slices.map((s) => this.assetClassLabelByKey(s.category));
        const data = slices.map((s) => Number(s.value ?? 0));
        const colorVars = [
            '--deepteal-500',
            '--deepteal-400',
            '--deepteal-200',
            '--deepteal-100',
            '--deepteal-50',
            '--lime-500',
            '--lime-700',
        ];
        const backgroundColor = labels.map((_, i) => style.getPropertyValue(colorVars[i % colorVars.length]).trim() || colorVars[i % colorVars.length]);
        this.doughnutChartData = {
            labels,
            datasets: [
                {
                    data,
                    backgroundColor,
                    hoverBackgroundColor: backgroundColor,
                },
            ],
        };
    }

    private assetClassLabelByKey(key: string | null | undefined): string {
        const k = String(key ?? '').trim();
        switch (k) {
            case 'PrivateEquity':
                return 'Private Equity';
            case 'PrivateCredit':
                return 'Private Credit';
            case 'DigitalAssets':
                return 'Digital Assets';
            case 'RealEstate':
                return 'Real Estate';
            case 'Commodities':
                return 'Commodities';
            case 'Collectibles':
                return 'Collectibles';
            default:
                return k || 'Other';
        }
    }
    dealProcessSteps: Step[] = [
        {
          id: 1,
          label: 'Express Interest',
          subLabel: '',
          tooltip: "How much you'd like to invest."
        },
        {
          id: 2,
          label: 'Min Threshold Met',
          subLabel: '',
          tooltip: 'Enough interest has been received for the deal to proceed.'
        },
        {
          id: 3,
          label: 'KYC Check',
          subLabel: '',
          tooltip: 'Verify your identity and eligibility to invest.'
        },
        {
          id: 4,
          label: 'Subscription',
          subLabel: '',
          tooltip: 'Sign the subscription documents to confirm your investment.'
        },
        {
          id: 5,
          label: 'Capital Call',
          subLabel: '',
          tooltip: 'Transfer your funds by the due date to complete your investment.'
        }
      ];

    public getDealProcessStepsForDeal(d: ClientDeal): Step[] {
        const kycStatus = Number((d as any)?.clientKyc?.status ?? (d as any)?.clientKycStatus ?? 0);
        const kycLabel =
            kycStatus === ClientKycStatus.SUBMITTED
                ? 'KYC Submitted'
                : kycStatus === ClientKycStatus.UNDER_REVIEW
                    ? 'KYC Under review'
                    : kycStatus === ClientKycStatus.APPROVED
                        ? 'KYC Approved'
                        : kycStatus === ClientKycStatus.REJECTED
                            ? 'KYC Rejected'
                        : 'KYC Check';

        return this.dealProcessSteps.map((s, idx) => (idx === 2 ? { ...s, label: kycLabel, subLabel: '' } : s));
    }


    public ngOnDestroy() {
        this.configUpdateSub.unsubscribe();
    }
}
